import { createFileRoute } from '@tanstack/react-router'
import { PageRenderer } from '@/features/core/components/PageRenderer'
import { EcommerceHomepage } from '@/features/ecommerce/components/EcommerceHomepage'
import type { Page, Product, SiteConfig } from '@/types'

interface HomeLoaderData {
  page: Page | null
  products: Product[]
  siteConfig: SiteConfig | null
  isEcommerce: boolean
}

export const Route = createFileRoute('/')({
  component: HomePage,
  loader: async (): Promise<HomeLoaderData> => {
    try {
      // Load pages, products, and site config in parallel
      const [pagesRes, productsRes, configRes] = await Promise.all([
        fetch('/data/pages.json'),
        fetch('/data/products.json'),
        fetch('/data/site-config.json'),
      ])

      let pages: Page[] = []
      let products: Product[] = []
      let siteConfig: SiteConfig | null = null

      try {
        pages = await pagesRes.json()
      } catch {
        // Pages might not exist
      }

      try {
        products = await productsRes.json()
      } catch {
        // Products might not exist
      }

      try {
        siteConfig = await configRes.json()
      } catch {
        // Config might not exist
      }

      const homePage = pages.find((p) => p.slug === 'home' || p.slug === '')
      const isEcommerce = siteConfig?.features?.ecommerce === true

      return {
        page: homePage || null,
        products,
        siteConfig,
        isEcommerce
      }
    } catch {
      return {
        page: null,
        products: [],
        siteConfig: null,
        isEcommerce: false
      }
    }
  },
})

function HomePage() {
  const { page, products, siteConfig, isEcommerce } = Route.useLoaderData()

  // If we have a CMS homepage, render it
  if (page) {
    return <PageRenderer page={page} />
  }

  // If ecommerce is enabled and we have products, show ecommerce homepage
  if (isEcommerce && products.length > 0) {
    return <EcommerceHomepage products={products} siteConfig={siteConfig} />
  }

  // Fallback placeholder
  return (
    <div className="container mx-auto px-4 py-16 text-center">
      <h1 className="text-4xl font-bold mb-4">Welcome</h1>
      <p className="text-gray-600">Content coming soon.</p>
    </div>
  )
}
